import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
} from '@mui/joy';
import { ArrowCounterClockwise as Undo, X as Clear, Check, X as Close } from '@phosphor-icons/react';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (signatureData: string) => void;
  fieldLabel?: string;
  signerName?: string;
}

export const SignatureModal: React.FC<SignatureModalProps> = ({
  open,
  onClose,
  onSave,
  fieldLabel = 'Signature',
  signerName = 'Signer',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [paths, setPaths] = useState<Array<Array<{x: number, y: number}>>>([]);
  const dprRef = useRef<number>(1);

  // Redraw canvas with all paths
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    const dpr = dprRef.current;

    // Clear canvas (use actual canvas size)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Reset the transform and scale
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    // Set drawing style
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Redraw all paths
    paths.forEach(path => {
      if (path.length > 0) {
        ctx.beginPath();
        ctx.moveTo(path[0].x, path[0].y);
        path.forEach(point => {
          ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      }
    });
  }, [paths]);

  // Setup canvas with proper DPR scaling
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;

    // Set the actual canvas size accounting for device pixel ratio
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    // Scale the drawing context to match device pixel ratio
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);

      // Set drawing style
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }

    // Don't override the CSS size - let it stay responsive
    // The canvas will use its CSS size from the style prop
  }, []);

  useEffect(() => {
    if (open && canvasRef.current) {
      setupCanvas();
      redrawCanvas();

      // Setup ResizeObserver to handle canvas resizing
      const resizeObserver = new ResizeObserver(() => {
        setupCanvas();
        redrawCanvas();
      });

      resizeObserver.observe(canvasRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [open, setupCanvas, redrawCanvas]);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Since we're scaling the context by DPR, we just need CSS pixel coordinates
    // The context scaling handles the DPR conversion
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    setIsEmpty(false);

    const coords = getCoordinates(e);
    setPaths(prev => [...prev, [coords]]);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;

    const coords = getCoordinates(e);
    setPaths(prev => {
      const newPaths = [...prev];
      const currentPath = newPaths[newPaths.length - 1];
      currentPath.push(coords);
      return newPaths;
    });

    // Draw the new point
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && paths.length > 0) {
      const currentPath = paths[paths.length - 1];
      if (currentPath.length > 0) {
        ctx.beginPath();
        ctx.moveTo(currentPath[currentPath.length - 1].x, currentPath[currentPath.length - 1].y);
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    setPaths([]);
    setIsEmpty(true);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) {
      const dpr = dprRef.current;
      // Clear the entire canvas accounting for DPR
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);
    }
  };

  const handleUndo = () => {
    if (paths.length > 0) {
      const newPaths = paths.slice(0, -1);
      setPaths(newPaths);
      setIsEmpty(newPaths.length === 0);
      redrawCanvas();
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const signatureData = canvas.toDataURL('image/png');
      onSave(signatureData);
      handleClear();
      onClose();
    }
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog data-joy-color-scheme="light"
        sx={{
          maxWidth: { xs: '90vw', sm: '600px' },
          width: '100%',
          p: 0, colorScheme: 'light',
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
            <Box>
              <Typography level="h4">{fieldLabel}</Typography>
              <Typography level="body-sm" textColor="text.secondary">
                {signerName} - Please sign below
              </Typography>
            </Box>
            <IconButton
              variant="plain"
              color="neutral"
              onClick={handleClose}
              aria-label="Close signature"
              sx={{ ml: 'auto' }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 1.5 }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: '200px', sm: '250px' },
              border: '2px solid',
              borderColor: 'neutral.300',
              borderRadius: 'sm',
              bgcolor: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                width: '100%',
                height: '100%',
                touchAction: 'none',
                cursor: 'crosshair',
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />

            {isEmpty && (
              <Typography
                level="body-lg"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'text.tertiary',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                Sign here
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Undo />}
              onClick={handleUndo}
              disabled={paths.length === 0}
              sx={{ flex: 1 }}
            >
              Undo
            </Button>
            <Button
              variant="outlined"
              color="danger"
              startDecorator={<Clear />}
              onClick={handleClear}
              disabled={isEmpty}
              sx={{ flex: 1 }}
            >
              Clear
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleClose}
            size="lg"
            sx={{ minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            startDecorator={<Check />}
            onClick={handleSave}
            disabled={isEmpty}
            size="lg"
            sx={{ minWidth: '120px' }}
          >
            Save Signature
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
